import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { OwnerInfoContent } from '../components/organisms/OwnerInfoStep';
import { RoleSelectionContent } from '../components/organisms/RoleSelectionStep';
import { MultiStepLayout } from '../components/templates/MultiStepLayout';
import { UserRole, OwnerInfo } from '../types/tenantScreening';

import IntroductionStep, {
  stepDescription as introDesc,
  stepTitle as introTitle,
} from './add-listing-steps/IntroductionStep';
import InputAddress, {
  stepDescription as addressDesc,
  stepTitle as addressTitle,
} from './add-listing-steps/step-1/InputAddress';
import PriceAndTerms, {
  stepDescription as step16Desc,
  stepTitle as step16Title,
} from './add-listing-steps/step-1/PriceAndTerms';
import PropertyBasicsIntro, {
  stepDescription as basicsIntroDesc,
  stepTitle as basicsIntroTitle,
} from './add-listing-steps/step-1/PropertyBasicsIntro';
import PropertyTypeSelection, {
  stepDescription as step15Desc,
  stepTitle as step15Title,
} from './add-listing-steps/step-1/PropertyTypeSelection';
import ListingDescription, {
  stepDescription as desc2Desc,
  stepTitle as desc2Title,
} from './add-listing-steps/step-2/ListingDescription';
import MakeItStandOutIntro, {
  stepDescription as intro2Desc,
  stepTitle as intro2Title,
} from './add-listing-steps/step-2/MakeItStandOutIntro';
import SelectAmenities, {
  stepDescription as amenitiesDesc,
  stepTitle as amenitiesTitle,
} from './add-listing-steps/step-2/SelectAmenities';
import UploadPhotos, {
  stepDescription as photosDesc,
  stepTitle as photosTitle,
} from './add-listing-steps/step-2/UploadPhotos';
import ApplicationFeeSettings, {
  stepDescription as contactDesc,
  stepTitle as contactTitle,
} from './add-listing-steps/step-3/ApplicationFeeSettings';
import ApplicationSettings, {
  stepDescription as appSettingsDesc,
  stepTitle as appSettingsTitle,
} from './add-listing-steps/step-3/ApplicationSettings';
import GoLiveIntro, {
  stepDescription as goLiveIntroDesc,
  stepTitle as goLiveIntroTitle,
} from './add-listing-steps/step-3/GoLiveIntro';
import ListingPreview, {
  stepDescription as previewDesc,
  stepTitle as previewTitle,
} from './add-listing-steps/step-3/ListingPreview';
import ScreeningPackage, {
  stepDescription as shareDesc,
  stepTitle as shareTitle,
} from './add-listing-steps/step-3/ScreeningPackage';

// Wrapper components for add listing flow
const UserRoleStepWrapper: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<UserRole | undefined>();

  const handleRoleChange = (role: UserRole) => {
    setSelectedRole(role);
    // In a real implementation, this would be passed up to parent state
  };

  return (
    <RoleSelectionContent
      selectedRole={selectedRole}
      onRoleChange={handleRoleChange}
      title='Your Role'
      description='Select your role to help us tailor the experience for you.'
      hideInternalHeader={true}
    />
  );
};

const OwnerInfoStepWrapper: React.FC = () => {
  const [ownerInfo, setOwnerInfo] = useState<Partial<OwnerInfo>>({});

  const handleOwnerInfoChange = (info: Partial<OwnerInfo>) => {
    setOwnerInfo(info);
    // In a real implementation, this would be passed up to parent state
  };

  return (
    <OwnerInfoContent
      ownerInfo={ownerInfo}
      onOwnerInfoChange={handleOwnerInfoChange}
      title='Property Owner Information'
      description="Please provide the property owner's contact information."
      hideInternalHeader={true}
    />
  );
};

// Define the step structure for major steps and sub-steps
const stepGroups = [
  // Step 0: Introduction (not part of a major step)
  [{ Component: IntroductionStep, title: introTitle, description: introDesc }],
  // Step 1: Property Basics (6 sub-steps)
  [
    {
      Component: PropertyBasicsIntro,
      title: basicsIntroTitle,
      description: basicsIntroDesc,
    }, // 1-1
    { Component: InputAddress, title: addressTitle, description: addressDesc }, // 1-2
    {
      Component: UserRoleStepWrapper,
      title: 'Your Role',
      description: 'Select your role to help us tailor the experience for you.',
    }, // 1-3
    {
      Component: OwnerInfoStepWrapper,
      title: 'Property Owner Information',
      description: "Please provide the property owner's contact information.",
    }, // 1-4
    {
      Component: PropertyTypeSelection,
      title: step15Title,
      description: step15Desc,
    }, // 1-5
    { Component: PriceAndTerms, title: step16Title, description: step16Desc }, // 1-6
  ],
  // Step 2: Make it stand out (4 sub-steps)
  [
    {
      Component: MakeItStandOutIntro,
      title: intro2Title,
      description: intro2Desc,
    }, // 2-1
    {
      Component: SelectAmenities,
      title: amenitiesTitle,
      description: amenitiesDesc,
    }, // 2-2
    { Component: UploadPhotos, title: photosTitle, description: photosDesc }, // 2-3
    {
      Component: ListingDescription,
      title: desc2Title,
      description: desc2Desc,
    }, // 2-4
  ],
  // Step 3: Go live (5 sub-steps)
  [
    {
      Component: GoLiveIntro,
      title: goLiveIntroTitle,
      description: goLiveIntroDesc,
    }, // 3-1
    {
      Component: ApplicationSettings,
      title: appSettingsTitle,
      description: appSettingsDesc,
    }, // 3-2
    { Component: ScreeningPackage, title: shareTitle, description: shareDesc }, // 3-3
    {
      Component: ApplicationFeeSettings,
      title: contactTitle,
      description: contactDesc,
    }, // 3-4
    {
      Component: ListingPreview,
      title: previewTitle,
      description: previewDesc,
    }, // 3-5
  ],
];

// Flatten all steps for navigation
const steps = stepGroups.flat();

// Map each step index to its major step and sub-step index
const stepIndexToMajorAndSub = (() => {
  const map: { major: number; sub: number }[] = [];
  stepGroups.forEach((group, majorIdx) => {
    group.forEach((_step, subIdx) => {
      map.push({ major: majorIdx, sub: subIdx });
    });
  });
  return map;
})();

const AddListingPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    if (currentStep === 0) {
      navigate('/');
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Progress bar logic
  let currentSubStepPercentage = 0;
  let showDividerInsteadOfProgress = false;
  let currentMajorStep = 0;
  const stepMeta = stepIndexToMajorAndSub[currentStep];
  if (stepMeta) {
    currentMajorStep = stepMeta.major;
    const sub = stepMeta.sub ?? 0;
    if (stepMeta.major === 0) {
      // Introduction step: show divider, no progress
      currentSubStepPercentage = 0;
      showDividerInsteadOfProgress = true;
    } else if (
      typeof stepMeta.major === 'number' &&
      stepMeta.major >= 0 &&
      stepMeta.major < stepGroups.length
    ) {
      // For major steps with sub-steps, calculate percentage
      const group = stepGroups[stepMeta.major] ?? [];
      if (group.length > 1) {
        const totalSubSteps = group.length;
        currentSubStepPercentage = Math.round(
          (sub / (totalSubSteps - 1)) * 100
        );
      } else {
        currentSubStepPercentage = 0;
      }
      showDividerInsteadOfProgress = false;
    } else {
      currentSubStepPercentage = 0;
      showDividerInsteadOfProgress = false;
    }
  } else {
    // Fallbacks if stepMeta is undefined
    currentMajorStep = 0;
    currentSubStepPercentage = 0;
    showDividerInsteadOfProgress = true;
  }

  // Get the step component safely
  let ComponentToRender: React.FC = () => <div>Error: Step not found</div>;
  let currentStepTitle = '';
  let currentStepDescription = '';
  let useWideLayout = false; // Flag for steps that need wider layout

  if (
    stepMeta &&
    currentStep >= 0 &&
    currentStep < steps.length &&
    steps[currentStep]?.Component
  ) {
    ComponentToRender = steps[currentStep].Component;

    // Check if this is step 1-5 (PropertyTypeSelection) or step 2-2 (SelectAmenities) which need wider layout
    // Step 1-5 is at index 5, Step 2-2 is at index 8 in the flattened steps array
    const isPropertyTypeSelectionStep = currentStep === 5;
    const isSelectAmenitiesStep = currentStep === 8;
    useWideLayout = isPropertyTypeSelectionStep || isSelectAmenitiesStep;

    // Exclude title and description for intro steps (1-1, 2-1, 3-1)
    // These are the first sub-step (sub=0) of major steps 1, 2, 3
    const isIntroStep = stepMeta.major >= 1 && stepMeta.sub === 0;

    if (!isIntroStep) {
      currentStepTitle = steps[currentStep].title || '';
      currentStepDescription = steps[currentStep].description || '';
    }
  }

  const isFinalStep = currentStep === steps.length - 1;

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleExitClick = () => {
    navigate('/');
  };

  return (
    <MultiStepLayout
      currentStep={currentMajorStep}
      total={stepGroups.length - 1}
      currentSubStepPercentage={currentSubStepPercentage}
      onHomeClick={handleHomeClick}
      onExitClick={handleExitClick}
      onBack={handleBack}
      onNext={handleNext}
      backDisabled={false}
      nextDisabled={isFinalStep}
      nextLabel={isFinalStep ? 'Publish Listing' : 'Next'}
      nextButtonColor={isFinalStep ? 'primary' : 'secondary'}
      showDividerInsteadOfProgress={showDividerInsteadOfProgress}
      title={currentStepTitle}
      description={currentStepDescription}
      wideLayout={useWideLayout}
    >
      <ComponentToRender />
    </MultiStepLayout>
  );
};

export default AddListingPage;
